import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject } from '@angular/core';
import {
  CustomRequest,
  LoggerPartialBody,
  Pagination,
  QueryParamsPagination,
  Request,
  RequestMethod,
  RequestOptions,
} from '@api/abstracts/models';
import { CACHE, CacheInput, IS_CACHE_ENABLED } from '@api/interceptors';
import { environment } from '@environments/environment';
import { ApiEndpoints } from '@environments/model';
import { LogLevel, LogsData } from '@shared/services/logs';
import { normalizeUrlPath } from '@shared/utils';
import { catchError, EMPTY, Observable, take, tap, throwError } from 'rxjs';

/**
 * Abstract base class for type-safe REST API services with built-in caching and error handling.
 *
 * @template T - The resource entity type
 *
 * @remarks
 * - HTTP errors are automatically logged and re-thrown
 * - All operations return cold observables (execute on subscription)
 * - Integrates with HTTP interceptors for caching and authentication
 *
 * @example
 * ```
 * interface User {
 *   id: string;
 *   name: string;
 *   email: string;
 * }
 *
 * @Injectable({ providedIn: 'root' })
 * export class UserService extends ApiServiceBase<User> {
 *   constructor() {
 *     super('users', 'default');
 *   }
 * }
 * ```
 */
export abstract class ApiServiceBase<T> {
  /** Logging service for HTTP operations tracing. */
  protected readonly logs = inject(LogsData);

  /** Angular HttpClient for HTTP operations. */
  private readonly http: HttpClient = inject(HttpClient);

  /** Resolved API base URL from environment configuration. */
  private readonly baseUrl: string;

  /**
   * Initializes the API service with endpoint path and base URL key.
   *
   * @param endpoint - API resource path (e.g., 'users', 'products')
   * @param api - Environment API base URL key (default: 'default')
   */
  protected constructor(
    protected endpoint: string,
    protected api: keyof ApiEndpoints = 'default'
  ) {
    this.baseUrl = environment.API.endpoint[api];
  }

  /**
   * Retrieves a single resource by ID.
   *
   * @param id - Resource identifier
   * @param queryOptions - Optional query parameters for filtering or expansion
   * @param cache - Optional caching configuration
   * @returns Observable emitting the resource
   *
   * @example
   * ```
   * userService.read('123', { expand: 'profile' }).subscribe((user) => console.log(user));
   * ```
   */
  read(id: string, queryOptions: Record<string, string | number> = {}, cache?: CacheInput): Observable<T> {
    return this.performGet<T>(id, queryOptions, cache);
  }

  /**
   * Retrieves a collection of resources, optionally scoped by parent ID.
   *
   * @param id - Optional parent resource identifier for scoped queries
   * @param queryOptions - Optional query parameters for filtering/sorting
   * @param cache - Optional caching configuration
   * @returns Observable emitting resource array
   *
   * @example
   * ```
   * // All users
   * userService.list().subscribe((users) => console.log(users));
   *
   * // Users in organization
   * userService.list('org-123', { role: 'admin' }).subscribe((users) => console.log(users));
   * ```
   */
  list(id: string = '', queryOptions: Record<string, string | number> = {}, cache?: CacheInput): Observable<T[]> {
    return this.performGet<T[]>(id, queryOptions, cache);
  }

  /**
   * Retrieves a paginated collection with metadata.
   *
   * @param queryOptions - Pagination parameters (default: page 1, size 50)
   * @param cache - Optional caching configuration
   * @returns Observable emitting pagination result with items and metadata
   *
   * @example
   * ```
   * productService.listPaginate({ page: '1', size: '25', search: 'laptop' })
   *   .subscribe((result) => console.log(result.items, result.total));
   * ```
   */
  listPaginate(
    queryOptions: QueryParamsPagination = { page: '1', size: '50' },
    cache?: CacheInput
  ): Observable<Pagination<T>> {
    return this.performGet<Pagination<T>>(undefined, queryOptions, cache);
  }

  /**
   * Creates a new resource on the server.
   *
   * @param item - Resource data without ID (server-generated)
   * @param cache - Optional caching configuration
   * @returns Observable emitting the created resource with generated ID
   *
   * @example
   * ```
   * const newUser = { name: 'John', email: 'john@example.com' };
   * userService.create(newUser).subscribe((created) => console.log(created.id));
   * ```
   */
  create<U = Omit<T, 'id'>>(item: U, cache?: CacheInput): Observable<T> {
    const options = this.buildRequestOptions(cache);
    const url = this.buildUrl();

    return this.http.post<T>(url, item, options).pipe(
      tap((response) => this.logger({ method: 'POST', url }, response, item as LoggerPartialBody)),
      catchError((error: HttpErrorResponse) => this.handleError(error, 'POST', url, item as LoggerPartialBody))
    );
  }

  /**
   * Deletes a resource by ID.
   *
   * @param id - Resource identifier to delete
   * @returns Observable that completes on successful deletion
   *
   * @example
   * ```
   * userService.delete('123').subscribe(() => console.log('Deleted'));
   * ```
   */
  delete(id: string): Observable<void> {
    const url = this.buildUrl(id);

    return this.http.delete<void>(url).pipe(
      take(1),
      tap((response) => this.logger({ method: 'DELETE', url }, response)),
      catchError((error: HttpErrorResponse) => this.handleError(error, 'DELETE', url))
    );
  }

  /**
   * Partially updates specific resource fields using JSON Merge Patch (RFC 7396).
   *
   * @param id - Resource identifier
   * @param item - Partial object with fields to update
   * @returns Observable emitting the complete updated resource
   *
   * @example
   * ```
   * userService.updateProp('123', { name: 'Jane' }).subscribe((user) => console.log(user));
   * ```
   */
  updateProp(id: string, item: Partial<T>): Observable<T> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/merge-patch+json' });
    const options = this.buildRequestOptions(undefined, headers);
    const url = this.buildUrl(id);

    return this.http.patch<T>(url, item, options).pipe(
      take(1),
      tap((response) => this.logger({ method: 'PATCH', url }, response, item as LoggerPartialBody)),
      catchError((error: HttpErrorResponse) => this.handleError(error, 'PATCH', url, item as LoggerPartialBody))
    );
  }

  /**
   * Updates multiple resources in a single bulk operation.
   *
   * @param collection - Array of resources to update (must include IDs)
   * @returns Observable emitting the updated collection
   *
   * @remarks
   * Atomicity depends on server implementation.
   *
   * @example
   * ```
   * const updates = [{ id: '1', status: 'active' }, { id: '2', status: 'active' }];
   * userService.updateCollection(updates).subscribe((users) => console.log(users));
   * ```
   */
  updateCollection(collection: T[]): Observable<T[]> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/merge-patch+json' });
    const options = this.buildRequestOptions(undefined, headers);
    const url = this.buildUrl();

    return this.http.patch<T[]>(url, collection, options).pipe(
      take(1),
      tap((response) => this.logger({ method: 'PATCH', url }, response, collection as LoggerPartialBody)),
      catchError((error: HttpErrorResponse) => this.handleError(error, 'PATCH', url, collection as LoggerPartialBody))
    );
  }

  /**
   * Executes a fully customizable HTTP request with advanced options.
   *
   * @template Y - Expected response type (must match responseType option)
   * @param request - Request configuration (method, URL, body, headers, options)
   * @returns Observable emitting the typed response
   *
   * @remarks
   * **Type Safety:** Generic `Y` is compile-time only. Ensure it matches the actual response:
   * - `responseType: 'json'` → custom object type
   * - `responseType: 'text'` → `string`
   * - `responseType: 'blob'` → `Blob`
   * - `responseType: 'arraybuffer'` → `ArrayBuffer`
   *
   * @example
   * ```
   * // JSON response
   * apiService.customRequest<UserStats>({ method: 'GET', url: 'stats' })
   *   .subscribe((stats) => console.log(stats));
   *
   * // Binary download
   * apiService.customRequest<Blob>({
   *   method: 'GET',
   *   url: 'report/download',
   *   options: { responseType: 'blob' }
   * }).subscribe((blob) => saveFile(blob));
   *
   * // External API
   * apiService.customRequest<ExternalData>({
   *   method: 'GET',
   *   externalUrl: 'https://api.external.com/data'
   * }).subscribe((data) => console.log(data));
   * ```
   */
  customRequest<Y>(request: CustomRequest): Observable<Y> {
    const requestUrl = request.url ? `/${request.url}` : '';
    const baseUrl = request.externalUrl || `${this.baseUrl}/${this.endpoint}${requestUrl}`;
    const url = normalizeUrlPath(baseUrl);

    const executeRequest = (
      method: keyof typeof RequestMethod,
      httpCall: Observable<Y>,
      body?: unknown
    ): Observable<Y> => {
      return httpCall.pipe(
        take(1),
        tap((response) => this.logger({ method, url }, response, body as LoggerPartialBody)),
        catchError((error: HttpErrorResponse) => this.handleError(error, method, url, body as LoggerPartialBody))
      );
    };

    switch (request.method) {
      case 'GET': {
        const options: Record<string, unknown> = {
          ...(request.options ?? {}),
          ...this.buildRequestOptions(request.cache, request.options?.headers),
        };
        const queryParams = this.objectToQuerystring(request.query ?? {});

        return this.http.get(`${url}${queryParams}`, options).pipe(
          tap((response) => this.logger({ method: 'GET', url }, response)),
          catchError((error: HttpErrorResponse) => this.handleError(error, 'GET', url))
        ) as Observable<Y>;
      }

      case 'POST':
        return executeRequest('POST', this.http.post<Y>(url, request.body ?? {}), request.body);

      case 'PUT':
        return executeRequest('PUT', this.http.put<Y>(url, request.body ?? {}), request.body);

      case 'PATCH':
        return executeRequest('PATCH', this.http.patch<Y>(url, request.body ?? {}), request.body);

      case 'DELETE':
        return executeRequest('DELETE', this.http.delete<Y>(url));

      default:
        return EMPTY;
    }
  }

  /**
   * Performs a generic HTTP GET request with configurable return type.
   *
   * @template R - Expected response type
   * @param id - Optional resource identifier
   * @param queryOptions - Query parameters
   * @param cache - Optional caching configuration
   * @returns Observable emitting the typed response
   */
  private performGet<R>(
    id?: string,
    queryOptions: Record<string, string | number> = {},
    cache?: CacheInput
  ): Observable<R> {
    const options = this.buildRequestOptions(cache);
    const url = this.buildUrl(id, queryOptions);

    return this.http.get<R>(url, options).pipe(
      tap((response) => this.logger({ method: 'GET', url }, response)),
      catchError((error: HttpErrorResponse) => this.handleError(error, 'GET', url))
    );
  }

  /**
   * Constructs a normalized URL for API requests.
   *
   * @param id - Optional resource identifier
   * @param queryOptions - Optional query parameters
   * @returns Normalized URL string
   */
  private buildUrl(id?: string, queryOptions: Record<string, string | number> = {}): string {
    const idSegment = id?.trim() ? `/${id}` : '';
    const queryParams = this.objectToQuerystring(queryOptions);
    return normalizeUrlPath(`${this.baseUrl}/${this.endpoint}${idSegment}${queryParams}`, { preserveTrailing: false });
  }

  /**
   * Converts an object to a URL-encoded query string.
   *
   * @param obj - Key-value pairs to serialize
   * @returns Query string prefixed with "?" or empty string
   *
   * @remarks
   * - Undefined values are excluded
   * - Object values are JSON-stringified before encoding
   */
  private objectToQuerystring(obj: Record<string, string | number>): string {
    if (!obj || Object.keys(obj).length === 0) {
      return '';
    }

    const params = Object.entries(obj)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => {
        const encodedKey = encodeURIComponent(key);
        const encodedValue =
          typeof value === 'object' ? encodeURIComponent(JSON.stringify(value)) : encodeURIComponent(value);
        return `${encodedKey}=${encodedValue}`;
      })
      .join('&');

    return params ? `?${params}` : '';
  }

  /**
   * Handles HTTP errors with logging and error propagation.
   *
   * @param error - HTTP error response
   * @param method - HTTP method that failed
   * @param url - Request URL
   * @param body - Optional request body for mutations
   * @returns Observable that immediately errors with the original HttpErrorResponse
   */
  private handleError(
    error: HttpErrorResponse,
    method: string,
    url: string,
    body?: LoggerPartialBody
  ): Observable<never> {
    this.logs.groupStart(LogLevel.DEBUG, true, '🔴 ApiServiceBase', `${method.toUpperCase()} ${url}`);
    if (body) {
      this.logs.debug('', 'Request Body', body);
    }
    this.logs.debug('', 'Error Details', {
      status: error.status,
      statusText: error.statusText,
      message: error.message,
      error: error.error as unknown,
    });
    this.logs.groupEnd(LogLevel.DEBUG);

    return throwError(() => error);
  }

  /**
   * Logs HTTP request and response for debugging.
   *
   * @param req - Request metadata (method, URL)
   * @param response - Server response payload
   * @param body - Optional request body for mutations
   */
  private logger(req: Request, response: unknown, body?: LoggerPartialBody): void {
    this.logs.groupStart(LogLevel.DEBUG, true, '📡 ApiServiceBase', `${req.method.toUpperCase()} ${req.url}`);
    if (body && ['POST', 'PUT', 'PATCH'].includes(req.method)) {
      this.logs.debug('', 'Request Body', body?.body ?? body);
    }
    this.logs.debug('', 'Response', response);
    this.logs.groupEnd(LogLevel.DEBUG);
  }

  /**
   * Builds HTTP context with caching configuration.
   *
   * @param cache - Optional cache configuration
   * @returns Object containing HttpContext with cache settings
   */
  private getCacheOptions(cache?: CacheInput): { context: HttpContext } {
    const context = new HttpContext();
    if (cache) {
      context.set(IS_CACHE_ENABLED, environment.isCacheEnabled);
      context.set(CACHE, {
        cacheExpirationTime: cache.cacheExpirationTime,
        forcedKey: cache.forcedKey,
        refresh: cache.refresh,
        store: cache.store,
      });
    }
    return { context };
  }

  /**
   * Builds HTTP request options with caching and headers.
   *
   * @param cache - Optional caching configuration
   * @param headers - Optional HTTP headers
   * @returns Complete RequestOptions object
   *
   * @remarks
   * HttpHeaders are immutable; each `set()` returns a new instance.
   */
  private buildRequestOptions(cache?: CacheInput, headers?: HttpHeaders): RequestOptions {
    const cacheOptions = this.getCacheOptions(cache);

    if (!headers) {
      return cacheOptions;
    }

    let finalHeaders = new HttpHeaders();

    headers.keys().forEach((key) => {
      const value = headers.get(key);
      if (value !== null) {
        finalHeaders = finalHeaders.set(key, value);
      }
    });

    return {
      ...cacheOptions,
      headers: finalHeaders,
    };
  }
}
